import { useCallback, useEffect, useState } from "react";
import { supabase } from "../auth/supabaseClient";
import {
  loadFinanceWarehouseReport,
  type FinanceWarehouseReport,
} from "../reports/financeWarehouseReport";

interface FinanceWarehouseReportPanelProps {
  farmId: string;
}

function formatToman(value: number): string {
  return `${new Intl.NumberFormat("fa-IR").format(value)} تومان`;
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat("fa-IR", { maximumFractionDigits: 4 }).format(value);
}

function StatCard({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <article className="manager-card">
      <p className="section-kicker">{label}</p>
      <h3>{value}</h3>
      {note ? <p>{note}</p> : null}
    </article>
  );
}

export function FinanceWarehouseReportPanel({ farmId }: FinanceWarehouseReportPanelProps) {
  const [report, setReport] = useState<FinanceWarehouseReport>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(undefined);
      setReport(await loadFinanceWarehouseReport(supabase, farmId));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "بارگذاری گزارش مالی انجام نشد.");
    } finally {
      setLoading(false);
    }
  }, [farmId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <section className="section-card" aria-labelledby="finance-warehouse-report-title">
      <div className="section-title-row">
        <div>
          <p className="section-kicker">M6C</p>
          <h2 id="finance-warehouse-report-title">گزارش مالی، انبار و دارایی</h2>
        </div>
        <button
          type="button"
          className="text-button"
          disabled={loading}
          onClick={() => void refresh()}
        >
          تازه‌سازی
        </button>
      </div>

      <p className="section-note">
        این گزارش فقط‌خواندنی است و از داده canonical V2 ساخته می‌شود. خریدهای قدیمی بر اساس دسته خرید
        از هزینه‌های عملیاتی جدا نمایش داده می‌شوند.
      </p>

      {loading ? <p className="section-note">در حال بارگذاری گزارش مالی…</p> : null}
      {error ? <p className="settings-warning">{error}</p> : null}

      {report ? (
        <>
          <div className="quick-grid">
            <StatCard label="فروش ثبت‌شده" value={formatToman(report.finance.postedSalesToman)} />
            <StatCard
              label="خرید ثبت‌شده"
              value={formatToman(report.finance.postedPurchasesToman)}
            />
            <StatCard
              label="هزینه ثبت‌شده"
              value={formatToman(report.finance.postedExpensesToman)}
            />
            <StatCard
              label="جمع اسناد فعال"
              value={formatToman(report.finance.postedTransactionToman)}
              note={`${formatNumber(report.finance.postedCount)} سند`}
            />
            <StatCard
              label="کل پرداخت‌های ثبت‌شده"
              value={formatToman(report.finance.recordedPaymentsToman)}
            />
            <StatCard
              label="پرداخت اسناد فعال"
              value={formatToman(report.finance.postedPaymentsToman)}
            />
            <StatCard
              label="مانده پرداخت‌نشده"
              value={formatToman(report.finance.unpaidBalanceToman)}
            />
            <StatCard label="اسناد باطل‌شده" value={formatNumber(report.finance.voidedCount)} />
          </div>

          <div className="manager-card">
            <p className="section-kicker">دارایی / تجهیزات</p>
            <div className="quick-grid">
              <StatCard label="تعداد دارایی" value={formatNumber(report.assets.assetCount)} />
              <StatCard label="دارایی فعال" value={formatNumber(report.assets.activeAssetCount)} />
              <StatCard label="جمع تعداد" value={formatNumber(report.assets.quantityTotal)} />
              <StatCard
                label="ارزش خرید ثبت‌شده"
                value={formatToman(report.assets.purchaseValueToman)}
              />
            </div>
          </div>

          <div className="manager-card">
            <p className="section-kicker">انبار مصرفی</p>
            {report.inventory.available ? (
              <div className="quick-grid">
                <StatCard
                  label="اقلام انبار"
                  value={formatNumber(report.inventory.itemCount ?? 0)}
                />
                <StatCard
                  label="اقلام فعال"
                  value={formatNumber(report.inventory.activeItemCount ?? 0)}
                />
                <StatCard
                  label="نیازمند سفارش"
                  value={formatNumber(report.inventory.lowStockCount ?? 0)}
                />
                <StatCard
                  label="گردش‌های ثبت‌شده"
                  value={formatNumber(report.inventory.movementCount ?? 0)}
                />
              </div>
            ) : (
              <p className="section-note">دسترسی خواندن انبار برای این حساب فعال نیست.</p>
            )}
          </div>
        </>
      ) : null}
    </section>
  );
}
